import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i3 from '@spartacus/cart/base/root';
import { MergeCartSuccessEvent, DeleteCartEvent, LoadCartEvent, RemoveCartEvent, CART_BASE_FEATURE } from '@spartacus/cart/base/root';
import * as i1 from '@spartacus/core';
import { Config, CxEvent, LanguageSetEvent, CurrencySetEvent, LogoutEvent, LoginEvent, facadeFactory, UserAddressEvent, UpdateUserAddressEvent, DeleteUserAddressEvent, OCC_USER_ID_ANONYMOUS, LoadUserAddressesEvent, GlobalMessageType, LoadUserPaymentMethodsEvent, UserActions, provideDefaultConfig, provideDefaultConfigFactory } from '@spartacus/core';
import { SaveCartSuccessEvent, RestoreSavedCartSuccessEvent } from '@spartacus/cart/saved-cart/root';
import { OrderPlacedEvent } from '@spartacus/order/root';
import { Subscription, merge } from 'rxjs';
import { filter, switchMap, map, take, tap } from 'rxjs/operators';
import * as i2 from '@ngrx/store';
import { HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var CheckoutStepType = /*#__PURE__*/function (CheckoutStepType) {
  CheckoutStepType["DELIVERY_ADDRESS"] = "deliveryAddress";
  CheckoutStepType["DELIVERY_MODE"] = "deliveryMode";
  CheckoutStepType["PAYMENT_DETAILS"] = "paymentDetails";
  CheckoutStepType["REVIEW_ORDER"] = "reviewOrder";
  return CheckoutStepType;
}(CheckoutStepType || {});
var CheckoutStepState = /*#__PURE__*/function (CheckoutStepState) {
  CheckoutStepState["COMPLETED"] = "completed";
  CheckoutStepState["SELECTED"] = "selected";
  CheckoutStepState["DISABLED"] = "disabled";
  return CheckoutStepState;
}(CheckoutStepState || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var DeliveryModePreferences = /*#__PURE__*/function (DeliveryModePreferences) {
  DeliveryModePreferences["FREE"] = "FREE";
  DeliveryModePreferences["LEAST_EXPENSIVE"] = "LEAST_EXPENSIVE";
  DeliveryModePreferences["MOST_EXPENSIVE"] = "MOST_EXPENSIVE";
  return DeliveryModePreferences;
}(DeliveryModePreferences || {});
let CheckoutConfig = /*#__PURE__*/(() => {
  class CheckoutConfig {
    static {
      this.ɵfac = function CheckoutConfig_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutConfig)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutConfig,
        factory: function CheckoutConfig_Factory(__ngFactoryType__) {
          let __ngConditionalFactory__ = null;
          if (__ngFactoryType__) {
            __ngConditionalFactory__ = new (__ngFactoryType__ || CheckoutConfig)();
          } else {
            __ngConditionalFactory__ = i0.ɵɵinject(Config);
          }
          return __ngConditionalFactory__;
        },
        providedIn: 'root'
      });
    }
  }
  return CheckoutConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultCheckoutConfig = {
  checkout: {
    steps: [{
      id: 'deliveryAddress',
      name: 'checkoutProgress.deliveryAddress',
      routeName: 'checkoutDeliveryAddress',
      type: [CheckoutStepType.DELIVERY_ADDRESS]
    }, {
      id: 'deliveryMode',
      name: 'checkoutProgress.deliveryMode',
      routeName: 'checkoutDeliveryMode',
      type: [CheckoutStepType.DELIVERY_MODE]
    }, {
      id: 'paymentDetails',
      name: 'checkoutProgress.paymentDetails',
      routeName: 'checkoutPaymentDetails',
      type: [CheckoutStepType.PAYMENT_DETAILS]
    }, {
      id: 'reviewOrder',
      name: 'checkoutProgress.reviewOrder',
      routeName: 'checkoutReviewOrder',
      type: [CheckoutStepType.REVIEW_ORDER]
    }],
    express: false,
    defaultDeliveryMode: [DeliveryModePreferences.FREE],
    guest: false
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultCheckoutRoutingConfig = {
  routing: {
    routes: {
      checkoutLogin: {
        paths: ['checkout-login'],
        authFlow: true
      },
      checkout: {
        paths: ['checkout']
      },
      checkoutDeliveryAddress: {
        paths: ['checkout/delivery-address']
      },
      checkoutDeliveryMode: {
        paths: ['checkout/delivery-mode']
      },
      checkoutPaymentDetails: {
        paths: ['checkout/payment-details']
      },
      checkoutReviewOrder: {
        paths: ['checkout/review-order']
      }
    }
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Emit this event to force checkout details reload
 */
let CheckoutQueryReloadEvent = /*#__PURE__*/(() => {
  class CheckoutQueryReloadEvent extends CxEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutQueryReloadEvent';
    }
  }
  return CheckoutQueryReloadEvent;
})();
/**
 * Emit this event to force checkout details reset
 */
let CheckoutQueryResetEvent = /*#__PURE__*/(() => {
  class CheckoutQueryResetEvent extends CxEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutQueryResetEvent';
    }
  }
  return CheckoutQueryResetEvent;
})();
/**
 * An abstract event for all the checkout events.
 */
class CheckoutEvent extends CxEvent {}
/**
 * An abstract event for all the delivery address related events.
 */
class CheckoutDeliveryAddressEvent extends CheckoutEvent {}
/**
 * Fired when the delivery address is create cleared.
 */
let CheckoutDeliveryAddressCreatedEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryAddressCreatedEvent extends CheckoutDeliveryAddressEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryAddressCreatedEvent';
    }
  }
  return CheckoutDeliveryAddressCreatedEvent;
})();
/**
 * Fired when the user sets a delivery address during checkout.
 */
let CheckoutDeliveryAddressSetEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryAddressSetEvent extends CheckoutDeliveryAddressEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryAddressSetEvent';
    }
  }
  return CheckoutDeliveryAddressSetEvent;
})();
/**
 * Fired when the delivery address has to be cleared.
 */
let CheckoutDeliveryAddressClearedEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryAddressClearedEvent extends CheckoutDeliveryAddressEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryAddressClearedEvent';
    }
  }
  return CheckoutDeliveryAddressClearedEvent;
})();
/**
 * An abstract event for all the delivery mode related events.
 */
class CheckoutDeliveryModeEvent extends CheckoutEvent {}
/**
 * Fired when the delivery mode was set.
 */
let CheckoutDeliveryModeSetEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryModeSetEvent extends CheckoutDeliveryModeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryModeSetEvent';
    }
  }
  return CheckoutDeliveryModeSetEvent;
})();
/**
 * Fired when the delivery mode has been cleared.
 */
let CheckoutDeliveryModeClearedEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryModeClearedEvent extends CheckoutDeliveryModeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryModeClearedEvent';
    }
  }
  return CheckoutDeliveryModeClearedEvent;
})();
/**
 * Fired when the delivery mode has an error when trying to be cleared.
 */
let CheckoutDeliveryModeClearedErrorEvent = /*#__PURE__*/(() => {
  class CheckoutDeliveryModeClearedErrorEvent extends CheckoutDeliveryModeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutDeliveryModeClearedErrorEvent';
    }
  }
  return CheckoutDeliveryModeClearedErrorEvent;
})();
/**
 * Emit this event to force delivery modes reload
 */
let CheckoutSupportedDeliveryModesQueryReloadEvent = /*#__PURE__*/(() => {
  class CheckoutSupportedDeliveryModesQueryReloadEvent extends CheckoutDeliveryModeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutSupportedDeliveryModesQueryReloadEvent';
    }
  }
  return CheckoutSupportedDeliveryModesQueryReloadEvent;
})();
/**
 * Emit this event to force delivery modes reset
 */
let CheckoutSupportedDeliveryModesQueryResetEvent = /*#__PURE__*/(() => {
  class CheckoutSupportedDeliveryModesQueryResetEvent extends CheckoutDeliveryModeEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutSupportedDeliveryModesQueryResetEvent';
    }
  }
  return CheckoutSupportedDeliveryModesQueryResetEvent;
})();
/**
 * An abstract event for all the payment details related events.
 */
class CheckoutPaymentDetailsEvent extends CheckoutEvent {}
/**
 * Fired when the payment details have been created.
 */
let CheckoutPaymentDetailsCreatedEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentDetailsCreatedEvent extends CheckoutPaymentDetailsEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentDetailsCreatedEvent';
    }
  }
  return CheckoutPaymentDetailsCreatedEvent;
})();
/**
 * Fired when the payment details have been set.
 */
let CheckoutPaymentDetailsSetEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentDetailsSetEvent extends CheckoutPaymentDetailsEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentDetailsSetEvent';
    }
  }
  return CheckoutPaymentDetailsSetEvent;
})();
/**
 * Emit this event to force payment card types reload
 */
let CheckoutPaymentCardTypesQueryReloadEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentCardTypesQueryReloadEvent extends CheckoutPaymentDetailsEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentCardTypesQueryReloadEvent';
    }
  }
  return CheckoutPaymentCardTypesQueryReloadEvent;
})();
/**
 * Emit this event to force payment card types reset
 */
let CheckoutPaymentCardTypesQueryResetEvent = /*#__PURE__*/(() => {
  class CheckoutPaymentCardTypesQueryResetEvent extends CheckoutPaymentDetailsEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CheckoutPaymentCardTypesQueryResetEvent';
    }
  }
  return CheckoutPaymentCardTypesQueryResetEvent;
})();
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutQueryEventListener = /*#__PURE__*/(() => {
  class CheckoutQueryEventListener {
    constructor(eventService) {
      this.eventService = eventService;
      this.subscriptions = new Subscription();
      this.onCheckoutQueryReload();
      this.onCheckoutQueryReset();
    }
    onCheckoutQueryReload() {
      this.subscriptions.add(merge(this.eventService.get(LanguageSetEvent), this.eventService.get(CurrencySetEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutQueryReloadEvent);
      }));
    }
    onCheckoutQueryReset() {
      this.subscriptions.add(merge(this.eventService.get(LogoutEvent), this.eventService.get(LoginEvent), this.eventService.get(SaveCartSuccessEvent), this.eventService.get(RestoreSavedCartSuccessEvent), this.eventService.get(MergeCartSuccessEvent), this.eventService.get(OrderPlacedEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutQueryEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutQueryEventListener)(i0.ɵɵinject(i1.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutQueryEventListener,
        factory: CheckoutQueryEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutQueryEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CHECKOUT_FEATURE = 'checkout';
const CHECKOUT_CORE_FEATURE = 'checkoutCore';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutDeliveryAddressFacade = /*#__PURE__*/(() => {
  class CheckoutDeliveryAddressFacade {
    static {
      this.ɵfac = function CheckoutDeliveryAddressFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutDeliveryAddressFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutDeliveryAddressFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutDeliveryAddressFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['getDeliveryAddressState', 'createAndSetAddress', 'setDeliveryAddress', 'clearCheckoutDeliveryAddress'],
          // TODO:#deprecation-checkout - remove once we remove ngrx
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutDeliveryAddressFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Checkout delivery address event listener.
 */
let CheckoutDeliveryAddressEventListener = /*#__PURE__*/(() => {
  class CheckoutDeliveryAddressEventListener {
    constructor(checkoutDeliveryAddressFacade, eventService, globalMessageService, activeCartFacade) {
      this.checkoutDeliveryAddressFacade = checkoutDeliveryAddressFacade;
      this.eventService = eventService;
      this.globalMessageService = globalMessageService;
      this.activeCartFacade = activeCartFacade;
      this.subscriptions = new Subscription();
      this.onDeliveryAddressCreated();
      this.onDeliveryAddressSet();
      this.onDeliveryAddressCleared();
      this.onUserAddressChange();
      this.onCartDeleted();
    }
    /**
     * Registers listeners for the User address events.
     */
    onUserAddressChange() {
      this.subscriptions.add(this.eventService.get(UserAddressEvent).pipe(filter(event => event instanceof UpdateUserAddressEvent || event instanceof DeleteUserAddressEvent), switchMap(({
        userId
      }) => this.activeCartFacade.takeActiveCartId().pipe(map(cartId => ({
        cartId,
        userId
      }))))).subscribe(({
        cartId,
        userId
      }) => {
        // we want to LL the checkout (if not already loaded), in order to clear the checkout data that's potentially set on the back-end
        this.checkoutDeliveryAddressFacade.clearCheckoutDeliveryAddress();
        this.eventService.dispatch({
          cartId,
          userId
        }, CheckoutSupportedDeliveryModesQueryResetEvent);
      }));
    }
    onDeliveryAddressCreated() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryAddressCreatedEvent).subscribe(({
        cartId,
        userId
      }) => {
        if (userId !== OCC_USER_ID_ANONYMOUS) {
          this.eventService.dispatch({
            userId
          }, LoadUserAddressesEvent);
        }
        this.globalMessageService.add({
          key: 'addressForm.userAddressAddSuccess'
        }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
        this.eventService.dispatch({
          userId,
          cartId
        }, CheckoutSupportedDeliveryModesQueryResetEvent);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    onDeliveryAddressSet() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryAddressSetEvent).subscribe(({
        userId,
        cartId
      }) => {
        this.eventService.dispatch({
          userId,
          cartId
        }, CheckoutSupportedDeliveryModesQueryResetEvent);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    onDeliveryAddressCleared() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryAddressClearedEvent).subscribe(() => this.eventService.dispatch({}, CheckoutQueryResetEvent)));
    }
    onCartDeleted() {
      this.subscriptions.add(this.eventService.get(DeleteCartEvent).subscribe(() => this.eventService.dispatch({}, CheckoutQueryResetEvent)));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutDeliveryAddressEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutDeliveryAddressEventListener)(i0.ɵɵinject(CheckoutDeliveryAddressFacade), i0.ɵɵinject(i1.EventService), i0.ɵɵinject(i1.GlobalMessageService), i0.ɵɵinject(i3.ActiveCartFacade));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutDeliveryAddressEventListener,
        factory: CheckoutDeliveryAddressEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutDeliveryAddressEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Checkout delivery mode event listener.
 */
let CheckoutDeliveryModeEventListener = /*#__PURE__*/(() => {
  class CheckoutDeliveryModeEventListener {
    constructor(eventService) {
      this.eventService = eventService;
      this.subscriptions = new Subscription();
      this.onDeliveryModeSet();
      this.onDeliveryModeCleared();
      this.onDeliveryModeClearedError();
      this.onDeliveryModeReset();
      this.onGetSupportedDeliveryModesQueryReload();
      this.onGetSupportedDeliveryModesQueryReset();
    }
    onDeliveryModeSet() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryModeSetEvent).subscribe(({
        userId,
        cartId,
        cartCode
      }) => {
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
        this.eventService.dispatch({
          userId,
          cartId,
          cartCode
        }, LoadCartEvent);
      }));
    }
    onDeliveryModeCleared() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryModeClearedEvent).subscribe(({
        userId,
        cartId,
        cartCode
      }) => {
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
        this.eventService.dispatch({
          userId,
          cartId,
          cartCode
        }, LoadCartEvent);
      }));
    }
    onDeliveryModeClearedError() {
      this.subscriptions.add(this.eventService.get(CheckoutDeliveryModeClearedErrorEvent).subscribe(({
        userId,
        cartId,
        cartCode
      }) => {
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
        this.eventService.dispatch({
          userId,
          cartId,
          cartCode
        }, LoadCartEvent);
      }));
    }
    /**
     * Registers listeners for the delivery mode clear event.
     * This is needed for when `CheckoutSupportedDeliveryModesQueryResetEvent` is dispatched
     * as we need to update the user's cart when the delivery mode is cleared from the backend checkout details.
     */
    onDeliveryModeReset() {
      this.subscriptions.add(this.eventService.get(CheckoutSupportedDeliveryModesQueryResetEvent).subscribe(({
        userId,
        cartId
      }) => this.eventService.dispatch({
        userId,
        cartId,
        /**
         * As we know the cart is not anonymous (precondition checked),
         * we can safely use the cartId, which is actually the cart.code.
         */
        cartCode: cartId
      }, LoadCartEvent)));
    }
    onGetSupportedDeliveryModesQueryReload() {
      this.subscriptions.add(merge(this.eventService.get(LanguageSetEvent), this.eventService.get(CurrencySetEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutSupportedDeliveryModesQueryReloadEvent);
      }));
    }
    onGetSupportedDeliveryModesQueryReset() {
      this.subscriptions.add(merge(this.eventService.get(LogoutEvent), this.eventService.get(LoginEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutSupportedDeliveryModesQueryResetEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutDeliveryModeEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutDeliveryModeEventListener)(i0.ɵɵinject(i1.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutDeliveryModeEventListener,
        factory: CheckoutDeliveryModeEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutDeliveryModeEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Checkout payment event listener.
 */
let CheckoutPaymentEventListener = /*#__PURE__*/(() => {
  class CheckoutPaymentEventListener {
    constructor(eventService, globalMessageService) {
      this.eventService = eventService;
      this.globalMessageService = globalMessageService;
      this.subscriptions = new Subscription();
      this.onPaymentCreated();
      this.onPaymentSet();
      this.onGetCardTypesQueryReload();
    }
    onPaymentCreated() {
      this.subscriptions.add(this.eventService.get(CheckoutPaymentDetailsCreatedEvent).subscribe(({
        userId
      }) => {
        if (userId !== OCC_USER_ID_ANONYMOUS) {
          this.eventService.dispatch({
            userId
          }, LoadUserPaymentMethodsEvent);
        }
        this.globalMessageService.add({
          key: 'paymentForm.paymentAddedSuccessfully'
        }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    onPaymentSet() {
      this.subscriptions.add(this.eventService.get(CheckoutPaymentDetailsSetEvent).subscribe(() => {
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    onGetCardTypesQueryReload() {
      this.subscriptions.add(merge(this.eventService.get(LanguageSetEvent), this.eventService.get(CurrencySetEvent)).subscribe(() => {
        this.eventService.dispatch({}, CheckoutPaymentCardTypesQueryReloadEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutPaymentEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutPaymentEventListener)(i0.ɵɵinject(i1.EventService), i0.ɵɵinject(i1.GlobalMessageService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutPaymentEventListener,
        factory: CheckoutPaymentEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutPaymentEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutPlaceOrderEventListener = /*#__PURE__*/(() => {
  class CheckoutPlaceOrderEventListener {
    constructor(eventService) {
      this.eventService = eventService;
      this.subscriptions = new Subscription();
      this.onOrderPlaced();
    }
    onOrderPlaced() {
      this.subscriptions.add(this.eventService.get(OrderPlacedEvent).subscribe(({
        userId,
        cartId,
        cartCode
      }) => {
        this.eventService.dispatch({
          userId,
          cartId,
          cartCode
        }, RemoveCartEvent);
        this.eventService.dispatch({}, CheckoutQueryResetEvent);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutPlaceOrderEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutPlaceOrderEventListener)(i0.ɵɵinject(i1.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutPlaceOrderEventListener,
        factory: CheckoutPlaceOrderEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutPlaceOrderEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * The event listener which dispatches legacy store actions.
 * It will be removed as soon as the legacy store is removed.
 */
// TODO:#deprecation-checkout remove once all the features using store are switched to c&q
let CheckoutLegacyStoreEventListener = /*#__PURE__*/(() => {
  class CheckoutLegacyStoreEventListener {
    constructor(eventService, store) {
      this.eventService = eventService;
      this.store = store;
      this.subscriptions = new Subscription();
      this.onUserAddressAction();
      this.onUserPaymentAction();
    }
    /**
     * Registers events for the user address actions.
     */
    onUserAddressAction() {
      this.subscriptions.add(this.eventService.get(LoadUserAddressesEvent).subscribe(({
        userId
      }) => {
        /**
         * TODO:#deprecation-checkout We have to keep this here, since the user address feature is still ngrx-based.
         * Remove once it is switched from ngrx to c&q.
         * We should dispatch an event, which will reload the userAddress$ query.
         */
        this.store.dispatch(new UserActions.LoadUserAddresses(userId));
      }));
    }
    /**
     * Registers events for the user payment actions.
     */
    onUserPaymentAction() {
      this.subscriptions.add(this.eventService.get(LoadUserPaymentMethodsEvent).subscribe(({
        userId
      }) => {
        this.store.dispatch(
        /**
         * TODO:#deprecation-checkout We have to keep this here, since the user payment feature is still ngrx-based.
         * Remove once it is switched from ngrx to c&q.
         * We should dispatch an event, which will load the userPayment$ query.
         */
        new UserActions.LoadUserPaymentMethods(userId));
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function CheckoutLegacyStoreEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutLegacyStoreEventListener)(i0.ɵɵinject(i1.EventService), i0.ɵɵinject(i2.Store));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutLegacyStoreEventListener,
        factory: CheckoutLegacyStoreEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutLegacyStoreEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutEventModule = /*#__PURE__*/(() => {
  class CheckoutEventModule {
    constructor(_checkoutQueryEventListener, _checkoutDeliveryAddressEventListener, _checkoutDeliveryModeEventListener, _checkoutPaymentEventListener, _checkoutPlaceOrderEventListener, _checkoutLegacyStoreEventListener) {
      // Intentional empty constructor
    }
    static {
      this.ɵfac = function CheckoutEventModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutEventModule)(i0.ɵɵinject(CheckoutQueryEventListener), i0.ɵɵinject(CheckoutDeliveryAddressEventListener), i0.ɵɵinject(CheckoutDeliveryModeEventListener), i0.ɵɵinject(CheckoutPaymentEventListener), i0.ɵɵinject(CheckoutPlaceOrderEventListener), i0.ɵɵinject(CheckoutLegacyStoreEventListener));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CheckoutEventModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return CheckoutEventModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Interceptor that handles "Cart not found" errors while a user is in a checkout step.
 *
 * When a user doing a checkout has a "Cart not found" error, he is redirected to checkout and the cart is reloaded.
 * If a "Cart not found" error happens and the user is not on checkout, this interceptor does not perform any actions.
 */
let CheckoutCartInterceptor = /*#__PURE__*/(() => {
  class CheckoutCartInterceptor {
    constructor(routingService, multiCartFacade) {
      this.routingService = routingService;
      this.multiCartFacade = multiCartFacade;
    }
    intercept(request, next) {
      return this.routingService.getRouterState().pipe(take(1), switchMap(state => {
        return next.handle(request).pipe(tap({
          error: response => {
            if (response instanceof HttpErrorResponse && this.isUserInCheckoutRoute(state.state?.semanticRoute)) {
              if (this.isCartNotFoundError(response)) {
                this.routingService.go({
                  cxRoute: 'cart'
                });
                const cartCode = this.getCartIdFromError(response);
                if (cartCode) {
                  this.multiCartFacade.reloadCart(cartCode);
                }
              }
            }
          }
        }));
      }));
    }
    /**
     * Returns true if the parameter semantic route is part of "checkout"
     * Checkout semantic routes:
     * checkout
     * checkoutPaymentType
     * CheckoutDeliveryAddress
     * checkoutDeliveryMode
     * checkoutPaymentDetails
     * checkoutReviewOrder
     * checkoutLogin
     * @param semanticRoute
     */
    isUserInCheckoutRoute(semanticRoute) {
      return semanticRoute?.toLowerCase().startsWith('checkout') ?? false;
    }
    /**
     * Checks of the error is for a cart not found, i.e. the cart doesn't exist anymore
     *
     * @param response
     */
    isCartNotFoundError(response) {
      return response.status === 400 && response.error?.errors?.[0]?.type === 'CartError' && response.error?.errors?.[0]?.reason === 'notFound';
    }
    getCartIdFromError(response) {
      return response.error?.errors?.[0]?.subject;
    }
    static {
      this.ɵfac = function CheckoutCartInterceptor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutCartInterceptor)(i0.ɵɵinject(i1.RoutingService), i0.ɵɵinject(i3.MultiCartFacade));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutCartInterceptor,
        factory: CheckoutCartInterceptor.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CheckoutCartInterceptor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const interceptors = [{
  provide: HTTP_INTERCEPTORS,
  useExisting: CheckoutCartInterceptor,
  multi: true
}];

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CHECKOUT_BASE_CMS_COMPONENTS = ['CheckoutOrchestrator', 'CheckoutOrderSummary', 'CheckoutProgress', 'CheckoutProgressMobileBottom', 'CheckoutProgressMobileTop', 'CheckoutDeliveryMode', 'CheckoutPaymentDetails', 'CheckoutPlaceOrder', 'CheckoutReviewOrder', 'CheckoutReviewPayment', 'CheckoutReviewShipping', 'CheckoutReviewOverview', 'CheckoutDeliveryAddress', 'GuestCheckoutLoginComponent'];
function defaultCheckoutComponentsConfig() {
  const config = {
    featureModules: {
      [CHECKOUT_FEATURE]: {
        cmsComponents: CHECKOUT_BASE_CMS_COMPONENTS,
        dependencies: [CART_BASE_FEATURE]
      },
      // by default core is bundled together with components
      [CHECKOUT_CORE_FEATURE]: CHECKOUT_FEATURE
    }
  };
  return config;
}
let CheckoutRootModule = /*#__PURE__*/(() => {
  class CheckoutRootModule {
    static {
      this.ɵfac = function CheckoutRootModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutRootModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CheckoutRootModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [...interceptors, provideDefaultConfig(defaultCheckoutRoutingConfig), provideDefaultConfig(defaultCheckoutConfig), provideDefaultConfigFactory(defaultCheckoutComponentsConfig)],
        imports: [CheckoutEventModule]
      });
    }
  }
  return CheckoutRootModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutBillingAddressFacade = /*#__PURE__*/(() => {
  class CheckoutBillingAddressFacade {
    static {
      this.ɵfac = function CheckoutBillingAddressFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutBillingAddressFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutBillingAddressFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutBillingAddressFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['setBillingAddress'],
          // TODO:#deprecation-checkout - remove once we remove ngrx
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutBillingAddressFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutDeliveryModesFacade = /*#__PURE__*/(() => {
  class CheckoutDeliveryModesFacade {
    static {
      this.ɵfac = function CheckoutDeliveryModesFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutDeliveryModesFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutDeliveryModesFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutDeliveryModesFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['getSupportedDeliveryModesState', 'getSupportedDeliveryModes', 'setDeliveryMode', 'getSelectedDeliveryModeState', 'clearCheckoutDeliveryMode'],
          // TODO:#deprecation-checkout - remove once we remove ngrx
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutDeliveryModesFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutPaymentFacade = /*#__PURE__*/(() => {
  class CheckoutPaymentFacade {
    static {
      this.ɵfac = function CheckoutPaymentFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutPaymentFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutPaymentFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutPaymentFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['getPaymentCardTypesState', 'getPaymentCardTypes', 'getPaymentDetailsState', 'createPaymentDetails', 'setPaymentDetails'],
          // TODO:#deprecation-checkout - remove once we remove ngrx
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutPaymentFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CheckoutQueryFacade = /*#__PURE__*/(() => {
  class CheckoutQueryFacade {
    static {
      this.ɵfac = function CheckoutQueryFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutQueryFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutQueryFacade,
        factory: () => (() => facadeFactory({
          facade: CheckoutQueryFacade,
          feature: CHECKOUT_CORE_FEATURE,
          methods: ['getCheckoutDetailsState']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CheckoutQueryFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/** AUGMENTABLE_TYPES_END */

/**
 * Generated bundle index. Do not edit.
 */

export { CHECKOUT_BASE_CMS_COMPONENTS, CHECKOUT_CORE_FEATURE, CHECKOUT_FEATURE, CheckoutBillingAddressFacade, CheckoutConfig, CheckoutDeliveryAddressClearedEvent, CheckoutDeliveryAddressCreatedEvent, CheckoutDeliveryAddressEvent, CheckoutDeliveryAddressEventListener, CheckoutDeliveryAddressFacade, CheckoutDeliveryAddressSetEvent, CheckoutDeliveryModeClearedErrorEvent, CheckoutDeliveryModeClearedEvent, CheckoutDeliveryModeEvent, CheckoutDeliveryModeEventListener, CheckoutDeliveryModeSetEvent, CheckoutDeliveryModesFacade, CheckoutEvent, CheckoutEventModule, CheckoutPaymentCardTypesQueryReloadEvent, CheckoutPaymentCardTypesQueryResetEvent, CheckoutPaymentDetailsCreatedEvent, CheckoutPaymentDetailsEvent, CheckoutPaymentDetailsSetEvent, CheckoutPaymentEventListener, CheckoutPaymentFacade, CheckoutQueryFacade, CheckoutQueryReloadEvent, CheckoutQueryResetEvent, CheckoutRootModule, CheckoutStepState, CheckoutStepType, CheckoutSupportedDeliveryModesQueryReloadEvent, CheckoutSupportedDeliveryModesQueryResetEvent, DeliveryModePreferences, defaultCheckoutComponentsConfig };
